/**
 * Shared CLI helpers — eliminates boilerplate across all commands.
 *
 * Error handling wraps core's throwing functions with process.exit(1).
 * Auth/workspace resolution delegates to core via the CLI's store instance.
 * Table formatting and JSON parsing are CLI-specific.
 */

import {
  ArbiError,
  ArbiApiError,
  getErrorMessage,
  getErrorCode,
  resolveAuth as coreResolveAuth,
  resolveWorkspace as coreResolveWorkspace,
  formatUserName,
  dm as dmOps,
  type AuthContext,
  type WorkspaceContext,
  type UserInfo,
} from '@arbidocs/sdk'
import { store, getConfig, resolveConfig } from './config.js'
import { hintUpdateOnError } from './version-check.js'
import { startBackgroundNotifications } from './notifications.js'
import { error as cliError, chalk } from './output.js'

// Re-export context types and shared utilities for convenience
export type { AuthContext, WorkspaceContext, UserInfo }
export { formatUserName }

// ── Error handling ────────────────────────────────────────────────────────────

// ── Connection error diagnosis ─────────────────────────────────────────────

/** Map Node.js error codes to actionable user hints. */
const CONNECTION_ERROR_HINTS: Record<string, string> = {
  ECONNREFUSED: 'Connection refused. Is the backend running?',
  ECONNRESET: 'Connection reset by server. The backend may have restarted.',
  ENOTFOUND: 'DNS resolution failed. Check the server URL.',
  ETIMEDOUT: 'Connection timed out. Check network connectivity.',
  UNABLE_TO_VERIFY_LEAF_SIGNATURE:
    'TLS certificate cannot be verified. The cert may be expired or self-signed.',
  CERT_HAS_EXPIRED: 'TLS certificate has expired. Renew with manage-deployment.',
  ERR_TLS_CERT_ALTNAME_INVALID: 'TLS certificate hostname mismatch. Check the server URL.',
  DEPTH_ZERO_SELF_SIGNED_CERT: 'Self-signed TLS certificate. The cert may need to be renewed.',
  SELF_SIGNED_CERT_IN_CHAIN: 'Self-signed certificate in chain. The cert may need to be renewed.',
}

/**
 * Turn a raw error into an actionable connection-error message.
 * Returns undefined if the error is not a connection-level problem.
 */
function diagnoseConnectionError(err: unknown): string | undefined {
  const code = getErrorCode(err)
  if (code && code in CONNECTION_ERROR_HINTS) {
    return CONNECTION_ERROR_HINTS[code]
  }

  // Catch generic "fetch failed" with no deeper cause code
  const msg = err instanceof Error ? err.message : ''
  if (msg === 'fetch failed' || msg.includes('fetch failed')) {
    return 'Network error connecting to the server. Run `arbi health` to diagnose.'
  }

  return undefined
}

/**
 * Format an error message with API error details if available.
 *
 * ArbiApiError.apiError may contain:
 *   - from authenticatedFetch:  { status, statusText }
 *   - from requireData/requireOk: the raw OpenAPI error object ({ detail: string, ... })
 *
 * `detail` is sometimes a string and sometimes itself a structured object
 * (e.g. FastAPI validation errors). We unwrap recursively so an agent never
 * sees `[object Object]` from a 422 — that's the single most common "I can't
 * tell what went wrong" complaint.
 */
export function formatCliError(err: unknown): string {
  // Check for connection-level errors first (gives the best message for "fetch failed")
  const connectionHint = diagnoseConnectionError(err)
  if (connectionHint) return connectionHint

  if (err instanceof ArbiApiError && err.apiError && typeof err.apiError === 'object') {
    const base = err.message
    const apiErr = err.apiError as Record<string, unknown>
    const detail = stringifyApiDetail(apiErr.detail ?? apiErr.message ?? apiErr.error ?? apiErr)
    if (detail && !base.includes(detail)) {
      return `${base} — ${detail}`
    }
    return base
  }
  return getErrorMessage(err)
}

/**
 * Squeeze a FastAPI/openapi error body into a single human-readable line.
 * Handles strings, arrays of {loc, msg, ...} validation errors, and nested
 * `{detail: …}` envelopes.
 */
function stringifyApiDetail(value: unknown): string {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (Array.isArray(value)) {
    // FastAPI 422 shape: [{loc: [...], msg: "...", type: "..."}, ...]
    const parts = value
      .map((v) => {
        if (typeof v === 'string') return v
        if (v && typeof v === 'object') {
          const rec = v as Record<string, unknown>
          const loc = Array.isArray(rec.loc) ? rec.loc.join('.') : undefined
          const msg = typeof rec.msg === 'string' ? rec.msg : undefined
          if (loc && msg) return `${loc}: ${msg}`
          return stringifyApiDetail(v)
        }
        return ''
      })
      .filter(Boolean)
    return parts.join('; ')
  }
  if (typeof value === 'object') {
    const rec = value as Record<string, unknown>
    if (typeof rec.detail === 'string') return rec.detail
    if (Array.isArray(rec.detail)) return stringifyApiDetail(rec.detail)
    if (typeof rec.message === 'string') return rec.message
    if (typeof rec.error === 'string') return rec.error
    // Last-ditch: serialize without exploding into [object Object]
    try {
      const s = JSON.stringify(value)
      // Don't return the empty-object placeholder
      return s === '{}' ? '' : s
    } catch {
      return ''
    }
  }
  return String(value)
}

/**
 * Wrap a command action — catches errors, formats message, exits.
 * Every command handler should use this instead of manual try/catch.
 */
export function runAction(fn: () => Promise<void>): () => Promise<void> {
  return async () => {
    try {
      await fn()
      process.exit(0)
    } catch (err) {
      cliError(`Error: ${formatCliError(err)}`)
      hintUpdateOnError()
      process.exit(1)
    }
  }
}

// ── Auth & workspace resolution ───────────────────────────────────────────────

/**
 * Authenticate and return the SDK client + config.
 * Auto-detects config if not already set, then delegates to core.
 */
export async function resolveAuth(): Promise<AuthContext> {
  try {
    // Ensure config exists (auto-detect if needed) before core auth
    resolveConfig()
    return await coreResolveAuth(store)
  } catch (err) {
    if (err instanceof ArbiError) {
      cliError(err.message)
      process.exit(1)
    }
    throw err
  }
}

/**
 * Authenticate, resolve workspace, and set up headers.
 * Auto-detects config if not already set, then delegates to core.
 *
 * @param skipNotifications - Skip background notification WS. Set to true
 *   when the command will open its own WebSocket (upload --watch, watch)
 *   to avoid session conflicts (backend enforces one WS per session).
 */
export async function resolveWorkspace(
  workspaceOpt?: string,
  { skipNotifications = false }: { skipNotifications?: boolean } = {}
): Promise<WorkspaceContext> {
  try {
    // Ensure config exists (auto-detect if needed) before core auth
    resolveConfig()
    const ctx = await coreResolveWorkspace(store, workspaceOpt)

    // Start background notifications if enabled (skip for commands with their own WS)
    if (!skipNotifications && getConfig()?.notifications !== false) {
      startBackgroundNotifications(ctx.config.baseUrl, ctx.accessToken).catch(() => {})
    }

    return ctx
  } catch (err) {
    if (err instanceof ArbiError) {
      cliError(err.message)
      process.exit(1)
    }
    throw err
  }
}

// ── DM crypto context ────────────────────────────────────────────────────────

/**
 * Authenticate and create a DM crypto context for E2E encrypted messaging.
 * Returns the arbi client, auth context, and crypto context together.
 */
export async function resolveDmCrypto() {
  const authCtx = await resolveAuth()
  const { arbi, loginResult } = authCtx
  const userExtId = arbi.session.getState().userExtId
  if (!userExtId) {
    cliError('No user ID in session — cannot set up DM encryption.')
    process.exit(1)
  }
  const crypto = dmOps.createDmCryptoContext(arbi, loginResult.signingPrivateKey, userExtId)
  return { ...authCtx, crypto }
}

// ── Table formatting ──────────────────────────────────────────────────────────

export interface Column {
  header: string
  width: number
  value: (row: Record<string, unknown>) => string
}

/**
 * Truncate a string to `width` characters, leaving room for a `…` marker so
 * callers can see at a glance that content was cut. Used by `printTable` and
 * by individual commands that render free-text columns (emails, message
 * bodies). Multi-byte characters count as 1.
 */
export function truncate(value: string, width: number): string {
  if (width <= 0) return ''
  if (value.length <= width) return value
  if (width === 1) return '…'
  return value.slice(0, width - 1) + '…'
}

/**
 * Print a formatted table with header + rows.
 * Truncates overflow with `…` so consumers can spot cut-off cells.
 */
export function printTable<T extends Record<string, unknown>>(columns: Column[], rows: T[]): void {
  // Header
  console.log(chalk.bold(columns.map((c) => c.header.padEnd(c.width)).join('')))

  // Rows
  for (const row of rows) {
    console.log(
      columns
        .map((c) => {
          const val = c.value(row as Record<string, unknown>) ?? ''
          // Reserve 1 char for trailing space so columns stay readable.
          return truncate(val, c.width - 1).padEnd(c.width)
        })
        .join('')
    )
  }
}

// ── JSON parsing ──────────────────────────────────────────────────────────────

/**
 * Parse a JSON string argument with a helpful error message.
 */
export function parseJsonArg<T = Record<string, unknown>>(input: string, example: string): T {
  try {
    return JSON.parse(input) as T
  } catch {
    cliError(`Invalid JSON. Example: ${example}`)
    process.exit(1)
  }
}

// ── Non-TTY & JSON helpers ───────────────────────────────────────────────────

/**
 * `true` when stdin is a real terminal. Used everywhere we'd otherwise pop an
 * inquirer prompt — agents pipe stdin closed and we must refuse instead of
 * hanging on (or worse, exit-0-on-cancel as the old `dm send` no-args path
 * did).
 */
export function isInteractive(): boolean {
  return Boolean(process.stdin.isTTY)
}

/**
 * Refuse to launch an interactive prompt when stdin isn't a TTY. Prints a
 * recovery hint to stderr and exits 1 — never returns. Call this at the top
 * of any command path that's about to call inquirer.
 */
export function requireInteractive(hint: string): void {
  if (isInteractive()) return
  cliError(`Cannot prompt: not running in a terminal. ${hint}`)
  process.exit(1)
}

/**
 * Pretty-print a JSON-serializable value to stdout with a trailing newline.
 * Keeping every command on the same printer means `--json` output is byte-for-
 * byte identical across the CLI (no stray colors, no missing newline) and the
 * shape stays parseable when piped to `| jq`.
 */
export function printJson(value: unknown): void {
  process.stdout.write(JSON.stringify(value, null, 2) + '\n')
}

/**
 * Suggest the canonical next command on a "not found" error. Keeps recovery
 * hints uniform — without them an agent reading "X not found." has nowhere
 * to go; with them, it knows to call `arbi <thing> --ids` or similar.
 */
export function notFoundHint(message: string, hintCommand: string): string {
  return `${message}\nTry: ${hintCommand}`
}

/**
 * Find the candidate name closest to `input`. Used for "Did you mean …?"
 * hints when commander's built-in suggestions don't fire (e.g. an unknown
 * subcommand sneaks past a parent command that accepts positional args).
 * Cheap leading-prefix match — good enough for short subcommand vocabularies.
 */
export function nearestMatch(input: string, candidates: string[]): string | null {
  if (!input || candidates.length === 0) return null
  const lower = input.toLowerCase()
  const prefix = candidates.find((c) => c.startsWith(lower))
  if (prefix) return prefix
  let best: { name: string; score: number } | null = null
  for (const c of candidates) {
    let score = 0
    for (let i = 0; i < Math.min(c.length, lower.length); i++) {
      if (c[i] === lower[i]) score++
      else break
    }
    if (!best || score > best.score) best = { name: c, score }
  }
  return best && best.score >= 1 ? best.name : null
}

/**
 * Print a "did you mean X?" message and exit 1. Shared by the parent
 * commands that have both a default action and subcommands (`dm`, `task`,
 * `agent`, `session`) — commander treats unknown subcommands as positional
 * args there and the default suggestion machinery skips them.
 */
export function suggestSubcommandAndExit(
  parentName: string,
  attempted: string,
  candidates: string[]
): never {
  const suggestion = nearestMatch(attempted, candidates)
  const hint = suggestion ? ` (Did you mean ${suggestion}?)` : ''
  cliError(
    `unknown subcommand '${attempted}' for '${parentName}'${hint}\nAvailable: ${candidates.join(', ')}`
  )
  process.exit(1)
}

// ── Shared list / picker primitives ──────────────────────────────────────────

/**
 * Print `data` as JSON when `opts.json` is set, otherwise let the caller render
 * the human table — but in either case route the empty-state message to stderr
 * so `arbi <thing> list | wc -l` stays honest. Returns `true` when the caller
 * should keep going (data has rows and we're not in JSON mode); `false` when
 * the function has already produced output. Collapses the 20+ near-identical
 * blocks that look like:
 *
 *   if (opts.json) { printJson(data); return }
 *   if (data.length === 0) { stderr.write('No X found.'); return }
 *   printTable(...)
 */
export function emitListOrTable<T>(
  data: T[],
  opts: { json?: boolean },
  emptyMessage: string
): boolean {
  if (opts.json) {
    printJson(data)
    return false
  }
  if (data.length === 0) {
    process.stderr.write(`${emptyMessage}\n`)
    return false
  }
  return true
}

/**
 * Resolve a user-supplied selector (id OR exact, case-insensitive name) against
 * a list of items that have an `external_id` and `name`. Used to be hand-rolled
 * twice — once in `workspaces.ts` for `workspace select`, once in `doctags.ts`
 * for tag refs — with subtly different ambiguity errors. Centralized so every
 * resolver behaves identically (exit code 3 on ambiguous-name or not-found,
 * matching IDs listed).
 */
export interface ResolveOk<T> {
  ok: true
  item: T
  id: string
}
export interface ResolveErr<T> {
  ok: false
  reason: 'not-found' | 'ambiguous'
  matches: T[]
}
export type ResolveResult<T> = ResolveOk<T> | ResolveErr<T>

export function resolveByIdOrName<T extends { external_id?: string; name?: string | null }>(
  list: T[],
  selector: string
): ResolveResult<T> {
  const byId = list.find((w) => w.external_id === selector)
  if (byId) return { ok: true, item: byId, id: byId.external_id as string }

  const lower = selector.toLowerCase()
  const byName = list.filter((w) => (w.name ?? '').toLowerCase() === lower)
  if (byName.length === 1) return { ok: true, item: byName[0], id: byName[0].external_id as string }
  if (byName.length > 1) return { ok: false, reason: 'ambiguous', matches: byName }
  return { ok: false, reason: 'not-found', matches: [] }
}

/**
 * Standard error path for `resolveByIdOrName`. Prints a recovery hint and
 * exits with code 3 ("target not found", distinct from auth/server failures
 * — see SKILL.md exit-code table).
 */
export function failResolveAndExit<T extends { external_id?: string; name?: string | null }>(
  thing: string,
  selector: string,
  res: ResolveErr<T>
): never {
  if (res.reason === 'ambiguous') {
    cliError(
      `Ambiguous ${thing} "${selector}" — matches ${res.matches
        .map((m) => m.external_id)
        .join(', ')}. Use the ID.`
    )
  } else {
    cliError(`${thing} "${selector}" not found.`)
  }
  process.exit(3)
}

/**
 * `--dry-run` output for destructive commands. The convention across the
 * CLI: when the caller passes `--dry-run`, print a `[dry-run]` line with
 * the verb and the resolved targets, then exit 0 WITHOUT calling the SDK.
 * Agents can preview the effect of a delete/remove/leave before committing.
 *
 * The actual SDK call site decides what to pass — for delete-by-id flows
 * that's the resolved ID list; for delete-account flows it's the email;
 * for delete-by-filter (`--all`) it's the count + a sample of IDs.
 */
export function dryRun(verb: string, targets: string | string[]): void {
  const list = Array.isArray(targets) ? targets : [targets]
  // Goes to stdout (the canonical preview for an agent) — matches the
  // success() pattern used after a real execution. The [dry-run] prefix
  // is a stable marker so an agent can branch on it.
  process.stdout.write(`[dry-run] Would ${verb}:\n`)
  for (const t of list) process.stdout.write(`  - ${t}\n`)
  process.stdout.write('[dry-run] (no changes made; drop --dry-run to execute)\n')
}

/**
 * Generic picker: fetch items, render them as inquirer choices, and let the
 * user pick one. Replaces the per-command `pickTag/pickDocs/pickConversation`
 * helpers that all duplicated this dance.
 *
 * Empty list → writes the supplied empty-state message to stderr and exits 0
 * (the calling command was about to operate on something — there's just
 * nothing to operate on, which isn't an error). Non-TTY → bails with the
 * supplied hint instead of hanging on inquirer.
 */
export async function pickFromList<T>(
  items: T[],
  toChoice: (item: T) => { name: string; value: string; description?: string },
  options: {
    message: string
    emptyMessage: string
    nonTtyHint: string
  }
): Promise<string> {
  if (items.length === 0) {
    process.stderr.write(`${options.emptyMessage}\n`)
    process.exit(0)
  }
  requireInteractive(options.nonTtyHint)
  // Lazy import so requireInteractive's exit path doesn't pull inquirer in.
  const { promptSelect } = await import('./prompts.js')
  return promptSelect(options.message, items.map(toChoice))
}
